// Tenant-side platform features: subscription, custom domains, support
// access, impersonation and announcements (docs/api/phase-1-platform.md).
import type { Envelope, Me } from '@bixo/ui'
import { queryOptions } from '@tanstack/react-query'
import { api } from '../../lib/api'

const data = <T>(promise: Promise<{ data: Envelope<T> }>): Promise<T> => promise.then((r) => r.data.data)

export type LimitKey = 'users' | 'branches' | 'invoices_per_month' | 'storage_mb' | 'ai_bill_scans_per_month'
export type SubscriptionStatus = 'trialing' | 'pending' | 'active' | 'past_due' | 'halted' | 'cancelled'

export interface PlanSummary {
  key: string
  name: string
  description: string | null
  price_monthly: string
  price_yearly: string
  limits: Record<LimitKey, number | null>
  features: Record<string, boolean>
}

export interface SubscriptionInfo {
  plan: { key: string; name: string }
  intended_plan: string
  billing_cycle: 'monthly' | 'yearly'
  status: SubscriptionStatus
  trial_ends_at: string | null
  current_period_end: string | null
  grace_ends_at: string | null
  checkout_url: string | null
  limits: Record<LimitKey, { limit: number | null; used: number | null }>
  features: Record<string, boolean>
  plans: PlanSummary[]
}

export interface CustomDomain {
  id: number
  domain: string
  status: 'pending' | 'verified' | 'failed'
  cname_ok: boolean
  failure_reason: string | null
  last_checked_at: string | null
  verified_at: string | null
  created_at: string
  instructions: { cname_name: string; cname_target: string; txt_name: string; txt_value: string }
}

export interface SupportGrant {
  id: number
  granted_by_name: string
  reason: string | null
  expires_at: string
  revoked_at: string | null
  created_at: string
}

export interface Announcement {
  id: number
  title: string
  body: string | null
  level: 'info' | 'warning' | 'critical'
}

export interface ImpersonationState {
  active: boolean
  operator_name: string | null
  read_only: boolean
  expires_at: string | null
}

/** Bixo's own invoice to this business for its subscription. */
export interface SubscriptionInvoice {
  id: number
  number: string
  plan_key: string
  billing_cycle: 'monthly' | 'yearly'
  period_start: string | null
  period_end: string | null
  subtotal: string
  tax: string
  total: string
  status: string
  issued_at: string
}

/** Download link for one of those invoices (same-origin, session cookie). */
export const subscriptionInvoiceUrl = (id: number, format: 'pdf' | 'html' = 'pdf') =>
  `/api/v1/subscription/invoices/${id}/${format}`

export const platformEndpoints = {
  subscriptionInvoices: () => data<SubscriptionInvoice[]>(api.get('/subscription/invoices')),
  subscription: () => data<SubscriptionInfo>(api.get('/subscription')),
  checkout: (body: { plan: string; cycle: 'monthly' | 'yearly' }) =>
    data<Omit<SubscriptionInfo, 'plans'>>(api.post('/subscription/checkout', body)),
  domains: () =>
    data<{ primary: string; cname_target: string; domains: CustomDomain[] }>(api.get('/domains')),
  addDomain: (domain: string) => data<CustomDomain>(api.post('/domains', { domain })),
  verifyDomain: (id: number) => data<CustomDomain>(api.post(`/domains/${id}/verify`)),
  removeDomain: (id: number) => api.delete(`/domains/${id}`).then(() => undefined),
  supportAccess: () =>
    data<{ active: SupportGrant | null; history: SupportGrant[] }>(api.get('/support-access')),
  grantSupport: (body: { hours: number; reason?: string }) =>
    data<SupportGrant>(api.post('/support-access', body)),
  revokeSupport: () => api.delete('/support-access').then(() => undefined),
  impersonation: () => data<ImpersonationState>(api.get('/impersonation')),
  endImpersonation: () => api.post('/impersonation/end').then(() => undefined),
  impersonate: (token: string) => data<{ user: Me }>(api.post('/auth/impersonate', { token })),
  announcements: () => data<Announcement[]>(api.get('/announcements')),
}

export const platformKeys = {
  subscription: ['platform', 'subscription'] as const,
  subscriptionInvoices: ['platform', 'subscription', 'invoices'] as const,
  domains: ['platform', 'domains'] as const,
  support: ['platform', 'support'] as const,
  impersonation: ['platform', 'impersonation'] as const,
  announcements: ['platform', 'announcements'] as const,
}

export const impersonationQuery = queryOptions({
  queryKey: platformKeys.impersonation,
  queryFn: platformEndpoints.impersonation,
  staleTime: 60_000,
  retry: false,
})

export const announcementsQuery = queryOptions({
  queryKey: platformKeys.announcements,
  queryFn: platformEndpoints.announcements,
  staleTime: 5 * 60_000,
  retry: false,
})
